<script>
  // d3 stuff 
  import { extent, max } from 'd3-array';
  import { axisBottom, axisLeft } from 'd3-axis';
  import { scaleLinear, scaleBand } from 'd3-scale';
  import { line as lineShape, curveStep } from 'd3-shape';
  import { monthTickValues, monthFormat, apMonthFormat } from '../utils/d3-utils';
  
  // Components
  import Axis from './Axis.svelte';
  import Footnote from './Footnote.svelte';
  import Sourceline from './Sourceline.svelte';
  import ChartHeadline from './ChartHeadline.svelte';

  // Other imports
  import { intcomma } from 'journalize';
  import { stripHtml } from './../utils/strip';
  import { randomString } from './../utils/random';

  import { selSystem } from '../store.js';

  // Data configuration
  export let title;
  export let subhead;
  export let data;
  export let attrs;
  export let id;
  export let footnote = null;
  export let sourceline = null;
  export let selectedAttrIndex = 0;
  $: selectedAttr = attrs[selectedAttrIndex];

  export let includeAverage;

  // Dimensions
  export let width;
  export let height = 300;
  export let margin = { top: 20, right: 10, bottom: 35, left: 25 };
  
  let filteredData;

  $: if($selSystem) {
    filteredData = data.filter(
      (d) =>
        (d.pwsid == $selSystem)
    )
    includeAverage = true;
  } else {
    filteredData = data.filter(
      (d) =>
        (d.pwsid == id)
    );
  }

  // list of PWSIDs that are the only one in the county
  let only_one_in_county = ['CA0310002,CA0310003,CA0310012,CA0310019,CA0310021',
  'CA0510004,CA0510005,CA0510006,CA0510016,CA0510017,CA0510019',
  'CA0810001',
  'CA1810001',
  'CA2310003',
  'CA2610001',
  'CA0110012,CA0110018,CA3810011',
  'CA4710011',
  'CA5110002',
  'CA5210004',
  'CA0']

  $: if(only_one_in_county.includes($selSystem)) {
    includeAverage = false
  }

  // Reactive data
  // $: filteredData = data.filter(
  //   (d) =>
  //      (d.pwsid == id)
  // );

  $: selectedData = filteredData.map((d) => ({
    date: d.reporting_month,
    supplier_name: d.supplier_name,
    // display_name: d.display_name,
    hydrologic_region: d.hydro_region,
    county: d.main_county,
    value: d[selectedAttr.value],
    average: d[selectedAttr.line_value]
  }));

  $: selectedSupplierName = selectedData[0].supplier_name;
  $: selectedCountyName = selectedData[0].county;

  // $:  console.log(selectedData)


  // if there is a 0 value, add a footnote
  $: flag = selectedData.some(d => d.value === 0)

  const titleId = `${randomString()}`;
  $: title = stripHtml(selectedAttr.headline);

  // Scales
  $: xRange = selectedData.map((d) => d.date);
  $: xScale = scaleBand()
      .domain(xRange)
      .range([0, width - margin.left - margin.right])
      .padding(0);
  $: bandwidth = xScale.bandwidth() - 2;

  $: yScaleType = selectedAttr.yScaleType || scaleLinear;
  $: yMin = selectedAttr.yMin || 0;
  $: yMax = includeAverage ? max(selectedData, (d) => Math.max(d.value, d.average)) : max(selectedData, (d) => Math.max(d.value));

  // $: yMax = max(selectedData, (d) => Math.max(d.value))
  $: yScale = yScaleType()
      .domain([yMin, yMax])
      .nice()
      .range([height - margin.top - margin.bottom, 0]);
  $: y0 = yScale(yMin);

  // Shapes
  // let drawLine;

  // if (includeAverage) {
    $: drawLine = lineShape()
    .x((d) => xScale(d.date) + bandwidth / 2)
      .y((d) => yScale(d.average))
      .curve(curveStep);
  // }


  $: desktopTicks = [xRange[0],xRange[2],xRange[4],xRange[6],xRange[8],xRange[11]]
  $: mobileTicks = [xRange[0],xRange[3],xRange[7],xRange[11]]
  
  // Axes
  export let formatYTicks = intcomma;

  // function to format dates on x-axis in utils/d3-utils.js

  let customTickFormat = (d,i)=>{
    if ((i==0)|(monthFormat(d)=="Jan.")){
      return `${monthFormat(d)} ${d.getFullYear()}`
    }else{
      return `${monthFormat(d)}`
    }
    ;
  }
</script>

<ChartHeadline text={selectedSupplierName} />

<div class="subhead">{subhead}</div>
{#if includeAverage}
<div class="key">
  <div class="key-item">
    <span class="supplier-rgpcd"></span> {selectedSupplierName}
  </div>
  <div class="key-item">
    <hr class="regional-rgpcd" /> {selectedCountyName} County average
  </div>
</div>
{/if}

<div class="inner-container" bind:clientWidth={width}>
  <svg {width} {height} role="graphics-document" aria-labelledby="{titleId}" class="line-timeseries">
    <title id="{titleId}">{title}</title>
    <g transform="translate({margin.left},{margin.top})">
      <g class="axes">
        <Axis
          axis={axisLeft}
          scale={yScale}
          className="axis--y"
          tickSize={-(width - margin.left - margin.right)}
          tickFormat={formatYTicks}
          ticks="5"
        />
        <Axis
          axis={axisBottom}
          scale={xScale}
          className="axis--x"
          yTranslate={height - margin.top - margin.bottom}
          tickFormat={customTickFormat}
          tickValues={width < 500 ? mobileTicks : desktopTicks}
        />
      </g>
      <g class="bars">
        {#each selectedData as obj, idx}
          <rect
            data-index={idx}
            data-x={obj.date}
            data-y={obj.value}
            data-class={obj.class}
            x={xScale(obj.date)}
            width={bandwidth}
            y={yScale(obj.value)}
            height={y0 - yScale(obj.value)}
            opacity="0.6"
            fill={selectedAttr.barColor}
          />
        {/each}
      </g>
      {#if includeAverage}
        <g class="average-line">
          <path
            stroke={selectedAttr.lineColor}
            stroke-width="3"
            stroke-linejoin="round"
            stroke-linecap="round"
            fill="none"
            d={drawLine(selectedData)}
          />
        </g>
      {/if}
    </g>
  </svg>
</div>

{#if flag}
  <Footnote text="Data not available for all months." />
{/if}
{#if footnote}
  <Footnote text={footnote} />
{/if}

{#if sourceline}
  <Sourceline text={sourceline} />
{/if}
